import { Button, ButtonGroup } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import type { RefetchFn } from "@msuite/ui/query";
import { type Dispatch, Fragment, type SetStateAction } from "react";
import { AbsencesInboxModals } from "./absences-inbox-modals";
import type { AbsenceState, AbsencesSortBy } from "./shared";

/** Props Interface */
interface AbsencesInboxMenuProps {
	onRefetch: RefetchFn;
	isRefetching: boolean;
	sortBy: AbsencesSortBy;
	setSortBy: Dispatch<SetStateAction<AbsencesSortBy>>;
	absenceState: AbsenceState;
	setAbsenceState: Dispatch<SetStateAction<AbsenceState>>;
	sortByDirection: "asc" | "desc";
	setSortByDirection: Dispatch<SetStateAction<"asc" | "desc">>;
	onlyUpcoming: boolean;
	setOnlyUpcoming: Dispatch<SetStateAction<boolean>>;
}

export function AbsencesInboxMenu({
	sortBy,
	setSortBy,
	absenceState,
	setAbsenceState,
	onRefetch,
	isRefetching,
	sortByDirection,
	setSortByDirection,
	onlyUpcoming,
	setOnlyUpcoming,
}: AbsencesInboxMenuProps) {
	/** Hooks */
	const addAbsenceModal = useDisclosure();
	const { t } = useTranslation();

	/** Render */
	return (
		<Fragment>
			<ButtonGroup isAttached={false}>
				<Menu
					items={() => [
						t("general.order_by"),
						{
							label: t("dates.start_date"),
							icon: icon("CalendarArrowDown"),
							isActive: sortBy === "startDate",
							onClick: () => setSortBy("startDate"),
						},
						{
							label: t("general_fields.created_at"),
							icon: icon("Calendar"),
							isActive: sortBy === "createdAt",
							onClick: () => setSortBy("createdAt"),
						},
						{
							label: t("profile.full_name"),
							icon: icon("User"),
							isActive: sortBy === "name",
							onClick: () => setSortBy("name"),
						},
						"divider",
						t("general.state"),
						{
							label: t("general.all"),
							icon: icon("Rows4"),
							isActive: absenceState === "all",
							onClick: () => setAbsenceState("all"),
						},
						{
							label: t("absences.state.approved"),
							icon: icon("Check"),
							isActive: absenceState === "approved",
							onClick: () => setAbsenceState("approved"),
						},
						{
							label: t("absences.state.rejected"),
							icon: icon("X"),
							isActive: absenceState === "rejected",
							onClick: () => setAbsenceState("rejected"),
						},
						{
							label: t("absences.state.pending"),
							icon: icon("Timer"),
							isActive: absenceState === "pending",
							onClick: () => setAbsenceState("pending"),
						},
						{
							label: t("absences.state.cancelled"),
							icon: icon("Ban"),
							isActive: absenceState === "cancelled",
							onClick: () => setAbsenceState("cancelled"),
						},
						"divider",
						{
							label: t("absences.only_show_upcoming"),
							icon: icon("CalendarClock"),
							isActive: onlyUpcoming,
							onClick: () => setOnlyUpcoming(!onlyUpcoming),
						},
						"divider",
						{
							label: t("tables.sort_ascending"),
							icon: icon("ArrowDown"),
							isActive: sortByDirection === "asc",
							onClick: () => setSortByDirection("asc"),
						},
						{
							label: t("tables.sort_descending"),
							icon: icon("ArrowUp"),
							isActive: sortByDirection === "desc",
							onClick: () => setSortByDirection("desc"),
						},
					]}
				>
					<Button icon={<Icon name="Funnel" />} />
				</Menu>
				<Button
					icon={<Icon name="RefreshCcw" />}
					onClick={onRefetch}
					isLoading={isRefetching}
				/>
				<Button
					icon={<Icon name="Plus" />}
					onClick={addAbsenceModal.onOpen}
				/>
			</ButtonGroup>
			<AbsencesInboxModals
				addAbsenceModal={addAbsenceModal}
				onRefetch={onRefetch}
			/>
		</Fragment>
	);
}
